import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  MessageSquare, 
  LogOut, 
  Loader2,
  MapPin,
  Star,
  Crown,
  Settings,
  Briefcase,
  DollarSign,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContractorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [bids, setBids] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, profileRes, bidsRes, jobsRes, unreadRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/contractors/${user.id}`),
        axios.get(`${API}/bids/my`),
        axios.get(`${API}/jobs?status=open&limit=10`),
        axios.get(`${API}/messages/unread-count`)
      ]);
      setStats(statsRes.data);
      setProfile(profileRes.data);
      setBids(bidsRes.data);
      setAvailableJobs(jobsRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-900/50 text-yellow-400 border-yellow-800',
      accepted: 'bg-green-900/50 text-green-400 border-green-800',
      rejected: 'bg-red-900/50 text-red-400 border-red-800'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950" data-testid="contractor-dashboard">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/jobs">
                <Button variant="ghost" size="icon" data-testid="search-jobs-btn" className="text-stone-400 hover:text-white">
                  <Search size={20} />
                </Button>
              </Link>
              <Link to="/messages" className="relative">
                <Button variant="ghost" size="icon" data-testid="messages-btn" className="text-stone-400 hover:text-white">
                  <MessageSquare size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </Link>
              <Link to="/contractor/profile">
                <Button variant="ghost" size="icon" data-testid="profile-btn" className="text-stone-400 hover:text-white">
                  <Settings size={20} />
                </Button>
              </Link>
              <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn" className="text-stone-400 hover:text-white">
                <LogOut size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome & Subscription */}
        <div className="flex flex-col lg:flex-row gap-6 mb-8">
          <div className="flex-1">
            <h1 className="font-heading text-3xl text-white uppercase">Welcome, {user?.name}</h1>
            <p className="text-stone-400 mt-1">Find work and grow your business</p>
          </div>
          
          {/* Subscription Status */}
          <div className="industrial-card p-4 flex items-center gap-4">
            {stats?.subscription_active ? (
              <>
                <Crown className="w-10 h-10 text-orange-500" />
                <div>
                  <p className="text-white font-heading uppercase">{stats.subscription_tier} Plan</p>
                  <p className="text-stone-400 text-sm">Active subscription</p>
                </div>
              </>
            ) : (
              <>
                <Crown className="w-10 h-10 text-stone-600" />
                <div>
                  <p className="text-stone-400">No active subscription</p>
                  <Link to="/subscription">
                    <Button 
                      size="sm" 
                      data-testid="upgrade-btn"
                      className="mt-1 bg-orange-500 hover:bg-orange-600 text-white text-sm"
                    >
                      Upgrade Now
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Bids', value: stats?.total_bids || 0, icon: Briefcase },
            { label: 'Accepted', value: stats?.accepted_bids || 0, icon: TrendingUp },
            { label: 'Rating', value: stats?.rating?.toFixed(1) || '0.0', icon: Star },
            { label: 'Reviews', value: stats?.total_reviews || 0, icon: MessageSquare }
          ].map((stat, index) => (
            <div key={index} className="industrial-card p-6">
              <div className="flex items-center justify-between">
                <stat.icon className="w-8 h-8 text-orange-500" />
                <span className="font-heading text-4xl text-orange-500">{stat.value}</span>
              </div>
              <p className="text-stone-400 mt-2 uppercase tracking-wide text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Your Bids */}
          <div>
            <h2 className="font-heading text-2xl text-white uppercase mb-4">Your Bids</h2>
            
            {bids.length === 0 ? (
              <div className="industrial-card p-8 text-center">
                <Briefcase className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                <p className="text-stone-400">No bids yet</p>
                <Link to="/jobs">
                  <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white">
                    Browse Jobs
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bids.slice(0, 5).map((bid) => (
                  <Link 
                    key={bid.id} 
                    to={`/jobs/${bid.job_id}`}
                    className="block industrial-card p-4 hover:border-orange-500/50"
                    data-testid={`bid-card-${bid.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{bid.message.substring(0, 50)}...</p>
                        <p className="text-stone-400 text-sm mt-1">
                          Bid: <span className="text-orange-500">${bid.amount.toLocaleString()}</span>
                        </p>
                      </div>
                      <Badge className={`${getStatusBadge(bid.status)} rounded-sm uppercase text-xs`}>
                        {bid.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Available Jobs */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-white uppercase">Available Jobs</h2>
              <Link to="/jobs" className="text-orange-500 hover:text-orange-400 text-sm">
                View All
              </Link>
            </div>
            
            {availableJobs.length === 0 ? (
              <div className="industrial-card p-8 text-center">
                <Search className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                <p className="text-stone-400">No jobs available right now</p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableJobs.slice(0, 5).map((job) => (
                  <Link 
                    key={job.id} 
                    to={`/jobs/${job.id}`}
                    className="block industrial-card p-4 hover:border-orange-500/50"
                    data-testid={`available-job-${job.id}`}
                  >
                    <h3 className="font-heading text-lg text-white uppercase">{job.title}</h3>
                    <div className="flex items-center gap-4 mt-2 text-stone-400 text-sm">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {job.location}
                      </span>
                      <span>{job.acreage} acres</span>
                    </div>
                    {job.budget_max && (
                      <p className="text-orange-500 mt-2">
                        ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
